import React, { useEffect, useMemo } from "react";
import _ from 'lodash';
import cx from 'classnames';
import styles from './entity.module.scss';
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import Reveal from "../../animation";
import InfiniteScroll from "../../infiniteScroll";
import { useSpring, animated } from "@react-spring/web";
import useMeasure from "react-use-measure";
import { fetchDataSourceChildren } from "src/redux/actions/dataSource";
import Truncate from "../../truncate";

interface EntityBrowserProps {
  mappings?: any[];
  className?: string;
  width: number;
  entity?: any;
  items?: any[];
  // eslint-disable-next-line no-unused-vars
  action?: (entity: any) => React.ReactNode;
  // eslint-disable-next-line no-unused-vars
  onSelection: (entity: any) => void;
  parent?: string;
  mapping?: string;
  onMappingSelection: (string: any) => void;
  selectedFolder?: string;
  loading?: boolean;
  setAuthToast?: any;
}

const EntityBrowser: React.FC<EntityBrowserProps> = ({ loading, className, width, items, action, onSelection, parent, mapping, onMappingSelection, selectedFolder, setAuthToast }) => {
  return (
    <div className={cx(styles.container, className)}>
      {items && items.map((item) => (
        <EntityRow key={item.id} entity={item} width={width} action={action} onSelection={onSelection} parent={parent} mapping={mapping || ''} onMappingSelection={onMappingSelection} selectedFolder={selectedFolder} setAuthToast={setAuthToast} />
      ))}
    </div>
  );
}


const MappingsBrowser: React.FC<EntityBrowserProps> = ({ mappings, className, width, action, onSelection, mapping, onMappingSelection, selectedFolder, setAuthToast }) => {

  const loading = useSelector((state: any) => state.dataSource.mappings.loading);

  return (
    <div className={cx(styles.container, styles.stacks)} style={{ height: selectedFolder ? "66vh" : "100vh" }}>
      <Reveal show={loading} className={styles.stack}>
        <MappingPlaceholder width={'50%'} />
        <MappingPlaceholder width={'70%'} />
        <MappingPlaceholder width={'60%'} />
        <MappingPlaceholder width={'50%'} />
      </Reveal>
      <Reveal show={!loading} className={styles.stack}>
        <EntityBrowser loading={loading} className={className} items={mappings} width={width} action={action} onSelection={onSelection} mapping={mapping} onMappingSelection={onMappingSelection} selectedFolder={selectedFolder} setAuthToast={setAuthToast} />
      </Reveal>
    </div>
  );
}

const FolderBrowser: React.FC<EntityBrowserProps> = ({ className, width, entity, action, onSelection, mapping, selectedFolder, onMappingSelection }) => {
  const dispatch = useDispatch();

  const dataSourceItems = useSelector((state: any) => state.dataSource.items);
  const source = dataSourceItems.find((item: any) => item.id === entity?.id);
  var items = source?.children?.items || [];
  const loading = source?.children?.loading && !source?.fetchedChildren;

  useEffect(() => {
    if (entity?.id) {
      dispatch(fetchDataSourceChildren({ account: entity.id, parent: "", showFile: false }));
    }
  }, [entity?.id]);

  return (
    <div className={cx(styles.container, styles.stacks)}>
      <InfiniteScroll className={styles.scroll}
        loading={loading}
        hasMore={false}
        loadMore={() => { }}
        loader={() => {
          return (
            <>
              <FolderPlaceholder />
            </>
          )
        }}
      >
        <EntityBrowser loading={loading} className={className} width={width} items={items} action={action} onSelection={onSelection} parent={entity?.id} mapping={mapping} selectedFolder={selectedFolder} onMappingSelection={onMappingSelection}/>
      </InfiniteScroll>
    </div>
  );
}

/**
 * Entity Renders
*/

interface EntityRowProps {
  entity: any;
  width: number;
  // eslint-disable-next-line no-unused-vars
  action?: (entity: any) => React.ReactNode;
  // eslint-disable-next-line no-unused-vars
  onSelection: (entity: any) => void;
  parent?: string;
  mapping: string;
  onMappingSelection: (id: string) => void;
  selectedFolder?: string;
  setAuthToast?: any;
}

const EntityRow: React.FC<EntityRowProps> = ({ entity, width, action, onSelection, parent, mapping, onMappingSelection, selectedFolder, setAuthToast }) => {
  const Component = useMemo(() => {
    const isStorage = entity?.storageType;

    if (isStorage) {
      return MappingRow;
    }
    return FolderRow;
  }, [entity]);


  if (!Component) {
    return null;
  }

  return (
    React.createElement(Component, { entity, width, action, onSelection, parent, mapping, onMappingSelection, selectedFolder, setAuthToast })
  );
}

const FolderRow: React.FC<EntityRowProps> = ({ entity, width, action, onSelection, parent, mapping, selectedFolder, onMappingSelection }) => {
  const [expanded, setExpanded] = React.useState(false);
  const [mountChildren, setMountChildren] = React.useState(false);
  const [containerRef, containerBounds] = useMeasure();

  const [childrenAnimation] = useSpring({
    opacity: expanded ? 1 : 0,
    height: expanded ? containerBounds.height : 0,
    from: { opacity: 0, height: 0 },
  }, [expanded, containerBounds]);

  const handleClick = () => {
    onMappingSelection(mapping);
    setExpanded(!expanded);
    if(!expanded){
      onSelection(entity);
    }
    setMountChildren(true);
  }

  const handleSelection = (e, entity) => {
    if (e) {
      e.stopPropagation();
    }
    onSelection(entity);
    onMappingSelection(mapping);
  }

  return (
    <>
      <div className={cx(styles.entity, styles.expandable, { [styles.expanded]: expanded || selectedFolder === entity?.id })} style={{ width: width }} onClick={handleClick}>
        <div className={styles.name}>
          <Truncate width={260}>{entity?.name}</Truncate>
        </div>
        <div className={styles.actions}>
          <button className={styles.action}>
            <i className={cx(styles.arrow, "paperflite-dropdown", {
              [styles.expandedArrow]: expanded || selectedFolder === entity?.id
            })}></i>
          </button>
          </div>
          <div className={cx(styles.actions, styles.secondaryAction)}>
          <button className={cx(styles.action)} onClick={(e) => handleSelection(e, entity)}>
            <i className={cx(styles.download, {
              "paperflite-radio-button-selected": selectedFolder === entity.id,
              "paperflite-radio-button-unselected": selectedFolder !== entity.id
            })}></i>
          </button>
        </div>
      </div>
      {mountChildren && (
        <animated.div style={childrenAnimation} className={styles.children}>
          <div ref={containerRef} className={styles.inner}>
            <FileBrowser entity={entity} width={width - 15} action={action} onSelection={onSelection} parent={parent} mapping={mapping} selectedFolder={selectedFolder} onMappingSelection={onMappingSelection}/>
          </div>
        </animated.div>
      )}
    </>
  );
}

const FileBrowser: React.FC<EntityBrowserProps> = ({ className, width, entity, action, onSelection, parent, mapping, selectedFolder, onMappingSelection }) => {

  const dispatch = useDispatch();
  useEffect(() => {
    if (entity?.id) {
      dispatch(fetchDataSourceChildren({ account: parent || entity.id, parent: parent ? entity.id : "", showFile: false }));
    }
  }, [entity?.id]);

  const dataSourceItems = useSelector((state: any) => state.dataSource.items);
  const source = dataSourceItems.find((item: any) => item.id === mapping);
  const loading = source?.children[entity.id]?.loading;

  var items = [];
  if (parent && source && source.children) {
    if (entity && entity.id) {
      items = source.children[entity.id]?.items;
    }
  }


  return (
    <InfiniteScroll
      className={styles.scroll}
      loading={loading}
      hasMore={false}
      loadMore={() => {}}
      loader={() => {
        return (
          <>
            <FolderPlaceholder />
          </>
        );
      }}
    >
      <EntityBrowser
        loading={loading}
        className={className}
        width={width}
        items={items}
        action={action}
        mapping={mapping}
        onSelection={onSelection}
        parent={parent}
        selectedFolder={selectedFolder}
        onMappingSelection={onMappingSelection}
      />
    </InfiniteScroll>
  );
}

const MappingRow: React.FC<EntityRowProps> = ({ entity, width, action, onSelection, mapping, onMappingSelection, selectedFolder, setAuthToast }) => {
  const [expanded, setExpanded] = React.useState(false);
  const [mountChildren, setMountChildren] = React.useState(false);
  const [containerRef, containerBounds] = useMeasure();

  const [childrenAnimation] = useSpring({
    opacity: expanded ? 1 : 0,
    height: expanded ? containerBounds.height : 0,
    from: { opacity: 0, height: 0 },
  }, [expanded, containerBounds]);

  const handleClick = () => {
    setExpanded(!expanded);
    if(entity.migrationStatus === "AUTH_ERROR" && setAuthToast && !expanded) {
      setAuthToast(true);
      setExpanded(false);
    }

    setMountChildren(true);
    onMappingSelection(entity.id);
  }

  return (
    <>
      <div
        className={cx(styles.entity, styles.expandable, {
          [styles.expanded]: expanded,
        })}
        style={{ width: width }}
        onClick={handleClick}
      >
        <div
          className={cx(styles.mappingIcon, styles[entity.storageType])}
        ></div>
        <div className={styles.name}>
          <Truncate width={260}>
            {entity?.name} {entity?.storageName}
          </Truncate>
        </div>
        <div className={cx(styles.actions, styles.secondaryAction)}>
          <button className={cx(styles.action)}>
            <i
              className={cx(styles.arrow, "paperflite-dropdown", {
                [styles.expandedArrow]:
                  expanded || selectedFolder === entity?.id,
              })}
            ></i>
          </button>
        </div>
      </div>
      {mountChildren && (
        <animated.div style={childrenAnimation} className={styles.children}>
          <div ref={containerRef} className={styles.inner}>
            <FolderBrowser
              entity={entity}
              width={width - 15}
              action={action}
              onSelection={onSelection}
              mapping={entity.id}
              selectedFolder={selectedFolder}
              onMappingSelection={onMappingSelection}
            />
          </div>
        </animated.div>
      )}
    </>
  );
}

export const MappingPlaceholder: React.FC<{ width: string }> = ({ width }) => {
  return (
    <div className={cx(styles.entity, styles.placeholder)} style={{ width: width }}>
      <div className={cx(styles.name, styles.block)}></div>
    </div>
  );
}

export const FolderPlaceholder: React.FC<any> = () => {
  return (
    <div className={cx(styles.entity, styles.asset, styles.placeholder)}>
      <div className={styles.inner}>
        <div className={styles.details}>
          <div className={cx(styles.name, styles.block)}></div>
          <div className={styles.meta}>
            <span className={styles.block} />
          </div>
        </div>
      </div>
    </div>
  );
}

export { EntityBrowser, MappingsBrowser };